'use client';

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { endpointsClient } from '@terra/shared-rest-clients-endpoints';
import type { EndpointDetectionsReturnDto } from '@terra/shared-contracts-endpoints';

export const ENDPOINT_DETECTIONS_QUERY_KEY = 'endpoint-detections';

const fetchEndpointDetections = async ({
  endpointId,
}: {
  endpointId: string;
}): Promise<EndpointDetectionsReturnDto> => {
  const response = await endpointsClient.getEndpointDetections({
    params: { id: endpointId },
  });

  if (response.status === 200) {
    return response.body;
  }

  throw new Error('Failed to load endpoint detections');
};

export const useEndpointDetections = ({
  endpointId,
  enabled = false,
}: {
  endpointId: string;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: [ENDPOINT_DETECTIONS_QUERY_KEY, endpointId],
    queryFn: () => fetchEndpointDetections({ endpointId }),
    enabled: Boolean(endpointId) && enabled,
    staleTime: 30000,
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [ENDPOINT_DETECTIONS_QUERY_KEY, endpointId],
      exact: true,
    });
  }, [endpointId, queryClient]);

  return {
    findings: result.data?.findings ?? [],
    signals: result.data?.signals ?? [],
    isLoading: result.isLoading,
    isError: result.isError,
    refetch: result.refetch,
    invalidate,
  };
};
